import { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import type { FleetStatus, Deployment, Repository } from '../types/api';

interface FleetData {
  status: FleetStatus | null;
  repositories: Repository[];
  deployments: Deployment[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

export function useFleetStatus(autoRefresh: boolean = true, refreshInterval: number = 30000) {
  const [fleetData, setFleetData] = useState<FleetData>({
    status: null,
    repositories: [],
    deployments: [],
    loading: true,
    error: null,
    lastUpdated: null,
  });

  const fetchFleetData = async () => {
    try {
      setFleetData(prev => ({ ...prev, loading: true, error: null }));

      // Fetch all data in parallel
      const [status, repositories, deployments] = await Promise.all([
        apiService.getFleetStatus(),
        apiService.getRepositories(),
        apiService.getDeployments().catch(() => []), // Deployments endpoint might not exist yet
      ]);

      setFleetData({
        status,
        repositories,
        deployments,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error) {
      setFleetData(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch fleet data',
        lastUpdated: new Date(),
      }));
    }
  };

  const refresh = () => {
    fetchFleetData();
  };

  useEffect(() => {
    fetchFleetData();

    if (autoRefresh) {
      const interval = setInterval(fetchFleetData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  return {
    ...fleetData,
    refresh,
  };
}